import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BoxIcon, ChevronDown } from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Estate } from '@/features/estates-property/api/estate';
import { i18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { getRenderValue } from '@/utils/getEmptyVisualValue';
import type { Locale } from '@/utils/getPriceWithCurrency';
import { getPriceWithCurrency } from '@/utils/getPriceWithCurrency';

import type { EstateAnalysis } from '../api/analytics';
import type { DealType } from '../api/offer-statistics';
import { useOfferStatistics } from '../api/offer-statistics';
import { TrendArrow } from './trend-arrow';

const DealTypes: {
  label: string;
  value: DealType;
}[] = [
  { label: i18n.t('sales'), value: 'sale' },
  { label: i18n.t('rent'), value: 'rent_gross' },
];

export const OfferStatistics = ({
  data: estate,
  defaultDealType = 'sale',
  estateAnalysis,
  hideOnPrint = false,
}: {
  data?: Estate;
  defaultDealType?: DealType;
  estateAnalysis: EstateAnalysis;
  hideOnPrint?: boolean;
}) => {
  const { t, i18n } = useTranslation();

  const timePeriodOptions = useMemo(
    () => [
      { label: t('lastXMonths', { months: 12 }), value: 12 },
      { label: t('lastXYears', { years: 2 }), value: 24 },
      { label: t('lastXYears', { years: 4 }), value: 48 },
    ],
    [t]
  );

  const [targetPrice, setTargetPrice] = useState<'price' | 'price_per_square_meter'>('price');
  const [dealType, setDealType] = useState(
    DealTypes.find(({ value }) => value === defaultDealType) || DealTypes[0]
  );
  const [timePeriod, setTimePeriod] = useState(timePeriodOptions[0]);

  const { data: offerStatistics } = useOfferStatistics(
    estateAnalysis,
    targetPrice,
    timePeriod.value
  );

  const dealTypeOptions = useMemo(() => {
    if (timePeriod.value === 12) {
      const nonEmptyRecords = offerStatistics?.['rent_gross']?.statisticsData?.filter(
        (el) => el.mean !== 0 || el.count !== 0
      );
      if (!nonEmptyRecords || nonEmptyRecords.length < 5) {
        return [DealTypes[0]]; // only [sale]
      }
    }
    return DealTypes;
  }, [offerStatistics, timePeriod]);

  const data = useMemo(() => offerStatistics?.[dealType.value], [offerStatistics, dealType]);

  return (
    <div className={cn(hideOnPrint && 'print:hidden')}>
      <h2 className="my-6 text-md font-bold text-primary-dark print:hidden">
        {t('offerStatistics')}
      </h2>
      <Card className="p-6 print:rounded-sm print:p-2">
        <div className="mb-8 flex flex-wrap justify-between print:mb-2">
          <div>
            <h2 className="text-md font-bold text-primary-dark print:text-sm print:leading-tight">
              {t('yearlyOfferPricesInLocation')} ({estate?.address?.city},{' '}
              {estate?.address?.postalCode})
            </h2>
            <p className="text-sm leading-normal text-text-secondary print:text-[8px]">
              {getRenderValue(data?.timeRange)}
            </p>
          </div>
          <div className="flex gap-3">
            <div className="flex flex-nowrap">
              <Button
                variant="contained"
                size="sm"
                className={cn(
                  'rounded-br-none rounded-tr-none text-black print:h-6 print:text-[10px]',
                  targetPrice === 'price' ? 'bg-primary-light' : 'bg-muted'
                )}
                onClick={() => setTargetPrice('price')}
              >
                {t('price')}
              </Button>
              <Button
                variant="contained"
                size="sm"
                className={cn(
                  'rounded-bl-none rounded-tl-none text-black print:h-6 print:text-[10px]',
                  targetPrice === 'price_per_square_meter' ? 'bg-primary-light' : 'bg-muted'
                )}
                onClick={() => setTargetPrice('price_per_square_meter')}
              >
                {t('pricePerSquareMeter')}
              </Button>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="print:h-6 print:text-[10px]">
                  <span>{dealType.label}</span>
                  <ChevronDown aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {dealTypeOptions.map((item) => (
                  <DropdownMenuItem key={item.value} onClick={() => setDealType(item)}>
                    <span>{item.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="print:h-6 print:text-[10px]">
                  <span>{timePeriod.label}</span>
                  <ChevronDown aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {timePeriodOptions.map((item) => (
                  <DropdownMenuItem key={item.value} onClick={() => setTimePeriod(item)}>
                    <span>{item.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="grid grid-cols-[260px_1fr] gap-6 max-sm:block max-sm:space-y-6 print:grid-cols-[150px_1fr] print:gap-2">
          <div className="space-y-4 print:space-y-1">
            {/* Average Sales/Rent Price */}
            {data?.avgPrice ? (
              <Card className="rounded-md bg-card-foreground p-4 text-white print:p-2">
                <div className="flex items-center gap-1">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-card/5 print:h-4 print:w-4">
                    <BoxIcon className="h-4 w-4 print:h-2 print:w-2" />
                  </div>
                  <p className="text-sm font-light print:text-[8px] print:leading-tight">
                    {dealType.value === 'sale' ? t('averageSalesPrice') : t('averageRentPrice')}
                  </p>
                </div>
                <div className="mt-2 flex items-center gap-1 print:mt-1">
                  <p className="text-lg print:text-sm print:leading-tight">
                    {data?.avgPrice?.value
                      ? getPriceWithCurrency(data.avgPrice.value, 'EUR', i18n.language as Locale, {
                          maximumFractionDigits: 0,
                        })
                      : '----'}
                  </p>
                  <div className="flex items-center gap-2 rounded-lg bg-primary-light/5 px-4 py-2 print:px-2 print:py-0 print:text-xs">
                    {data?.avgPrice?.changePercent !== undefined ? (
                      <>
                        <TrendArrow changeValue={data?.avgPrice.changePercent} />
                        <p>{data?.avgPrice.changePercent.toFixed(2)}%</p>
                      </>
                    ) : (
                      '----'
                    )}
                  </div>
                </div>
              </Card>
            ) : null}
            {/* Last month's median price */}
            {data?.lastMonthMedianPrice ? (
              <Card className="rounded-md bg-card-foreground p-4 text-white print:p-2">
                <div className="flex items-center gap-1">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-card/5 print:h-4 print:w-4">
                    <BoxIcon className="h-4 w-4 print:h-2 print:w-2" />
                  </div>
                  <p className="text-sm font-light print:text-[8px] print:leading-tight">
                    {t('lastMonthMedianPrice')}
                  </p>
                </div>
                <div className="mt-2 flex items-center gap-1 print:mt-1">
                  <p className="text-lg print:text-sm print:leading-tight">
                    {data?.lastMonthMedianPrice?.value
                      ? getPriceWithCurrency(
                          data.lastMonthMedianPrice.value,
                          'EUR',
                          i18n.language as Locale,
                          {
                            maximumFractionDigits: 0,
                          }
                        )
                      : '----'}
                  </p>
                  <div className="flex items-center gap-2 rounded-lg bg-primary-light/5 px-4 py-2 print:px-2 print:py-0 print:text-xs">
                    {data?.lastMonthMedianPrice?.changePercent !== undefined ? (
                      <>
                        <TrendArrow changeValue={data?.lastMonthMedianPrice.changePercent} />
                        <p>{data?.lastMonthMedianPrice.changePercent.toFixed(2)}%</p>
                      </>
                    ) : (
                      '----'
                    )}
                  </div>
                </div>
              </Card>
            ) : null}
            {/* Last 4 years % change */}
            {data?.last4YPercentChange !== undefined ? (
              <Card className="rounded-md bg-card-foreground p-4 text-white print:p-2">
                <div className="flex items-center gap-1">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-card/5 print:h-4 print:w-4">
                    <BoxIcon className="h-4 w-4 print:h-2 print:w-2" />
                  </div>
                  <p className="text-sm font-light print:text-[8px] print:leading-tight">
                    {t('lastXYearsPercentChange', { years: 4 })}
                  </p>
                </div>
                <div className="mt-2 flex items-center gap-1 print:mt-1">
                  <p className="text-lg print:text-sm print:leading-tight">
                    {data.last4YPercentChange.toFixed(2)}%
                  </p>
                  {data?.last4YPercentChange !== 0 ? (
                    <div className="flex items-center gap-2 rounded-lg bg-primary-light/5 px-4 py-2 print:px-2 print:py-0 print:text-xs">
                      <TrendArrow changeValue={data?.last4YPercentChange} />
                    </div>
                  ) : null}
                </div>
              </Card>
            ) : null}
          </div>
          <div className="h-[440px] w-full print:h-[230px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={data?.statisticsData}
                margin={{ top: 20, right: 30, left: 50, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.1} />
                  </linearGradient>
                </defs>

                <XAxis
                  dataKey="label"
                  strokeWidth={0}
                  interval={0}
                  tick={({ x, y, payload }) => (
                    <text
                      x={x}
                      y={y}
                      dy={16}
                      textAnchor="middle"
                      className="mt-2 text-xs text-neutral-500"
                      fill="currentcolor"
                    >
                      {payload.value}
                    </text>
                  )}
                />
                <YAxis
                  interval={0}
                  strokeWidth={0}
                  tick={({ x, y, payload }) => (
                    <text
                      x={x}
                      y={y}
                      dy={4}
                      dx={-80}
                      className="text-md font-normal"
                      textAnchor="start"
                      fill="currentcolor"
                    >
                      {getPriceWithCurrency(payload.value, 'EUR', i18n.language as Locale, {
                        maximumFractionDigits: 0,
                      })}
                    </text>
                  )}
                />
                <CartesianGrid vertical={false} strokeWidth={1} strokeDasharray={4} />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const item = payload[0];
                      return (
                        <Card className="max-w-48 rounded-md bg-card-foreground p-4 text-[16px] text-white">
                          <div className="flex items-center gap-1">
                            <p className="font-bold">
                              {getPriceWithCurrency(
                                item.payload.mean,
                                'EUR',
                                i18n.language as Locale,
                                {
                                  maximumFractionDigits: 0,
                                }
                              )}
                            </p>
                            {item.payload.prevPeriod ? (
                              <div className="flex items-center gap-2 rounded-lg bg-primary-light/5 px-2 py-0.5">
                                <TrendArrow changeValue={item.payload.prevPeriod.changePercent} />
                                <p>{item.payload.prevPeriod.changePercent?.toFixed(2)}%</p>
                              </div>
                            ) : null}
                          </div>
                          <p>{t('xProperties', { count: item.payload.count })}</p>
                          <p>{t('xOnTheMarket', { count: item.payload.daysOnMarket })}</p>
                        </Card>
                      );
                    }
                    return null;
                  }}
                />
                <Area
                  type="linear"
                  dataKey="mean"
                  stroke="hsl(var(--primary))"
                  fillOpacity={1}
                  fill="url(#colorValue)"
                  strokeWidth={4}
                  connectNulls={false}
                  activeDot={{ r: 10 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>
    </div>
  );
};
